import React, { useCallback, useEffect, useState } from 'react';
import { SelectOptGroup, SelectOption } from 'components/common/input-components/select';
import ControlledSelect from 'components/common/form-controlled/controlled-select';
import ControlledTextInput from 'components/common/form-controlled/controlled-text-input';
import { FormattedMessage } from 'react-intl';
import { LoadingOutlined } from '@ant-design/icons';
import { useGitRevisions } from './use-git-revisions.hook';
import type { VCSForm } from 'components/templates/templates-wizard/common/template-wizard.types';
import type { GitProviderTypes } from 'types/api.types';
import type { TooltipProps } from 'components/common/tooltip';
import { useRevisionSearch } from 'hooks/use-revision-search.hook';
import useStores from 'hooks/use-stores.hooks';

type RevisionsComponentProps = {
  gitProvider: GitProviderTypes;
  vcsForm: VCSForm;
  disabled?: boolean;
  tooltip?: TooltipProps;
};

type Props = RevisionsComponentProps & {
  showDropdown: boolean;
};

const getCommonInputProps = (gitProviderType: GitProviderTypes) => ({
  inline: true,
  width: 50,
  label: {
    id: `templates.add.new.field.revision.${gitProviderType === 'HelmRepository' ? 'helmRepo' : 'git'}`
  },
  info: { id: `templates.add.new.field.revision.${gitProviderType === 'HelmRepository' ? 'helmRepo' : 'git'}.info` },
  name: 'revision'
});

const RevisionsSelectField: React.FunctionComponent<Props> = ({
  gitProvider,
  vcsForm,
  showDropdown,
  disabled,
  tooltip
}) => {
  return showDropdown ? (
    <RevisionDropdown gitProvider={gitProvider} vcsForm={vcsForm} disabled={disabled} tooltip={tooltip} />
  ) : (
    <RevisionTextBox gitProvider={gitProvider} vcsForm={vcsForm} disabled={disabled} tooltip={tooltip} />
  );
};

const RevisionDropdown: React.FunctionComponent<RevisionsComponentProps> = ({
  gitProvider,
  vcsForm,
  disabled,
  tooltip
}) => {
  const { blueprintsStore } = useStores();
  // GitHub Enterprise lists are capped, so a revision past the cap is searched for, or typed
  const allowUnlistedRevision = gitProvider === 'GitHubEnterprise';
  const { availableRevisions: listedRevisions, revisionsLoading } = useGitRevisions({
    vcsForm,
    hideNotification: allowUnlistedRevision
  });
  const { repository, vcsConnectionId } = vcsForm.watch();
  const [revisionSearch, setRevisionSearch] = useState('');
  const searchRevisions = useCallback(
    (search: string) =>
      blueprintsStore.loadVcsRevisions(
        { repository, vcsConnectionId: vcsConnectionId ?? undefined, search },
        { hideNotification: true }
      ),
    [blueprintsStore, repository, vcsConnectionId]
  );
  const { revisions: availableRevisions, searching } = useRevisionSearch({
    availableRevisions: listedRevisions,
    search: revisionSearch,
    queryKey: ['vcs-revisions', repository, vcsConnectionId],
    searchRevisions: allowUnlistedRevision && vcsConnectionId ? searchRevisions : undefined
  });

  useEffect(() => setRevisionSearch(''), [repository, vcsConnectionId]);
  const isUnlistedRevision =
    Boolean(revisionSearch) &&
    ![...(availableRevisions?.heads ?? []), ...(availableRevisions?.tags ?? [])].includes(revisionSearch);

  const RevisionsOptGroup = (keyName: string, labelName: string, revisions: string[] | undefined) => {
    return (
      <SelectOptGroup key={keyName} label={labelName} data-e2e={`git-vcs-revision-select-group-${labelName}`}>
        {revisions &&
          revisions.map(revision => (
            <SelectOption key={revision} value={revision}>
              {revision}
            </SelectOption>
          ))}
      </SelectOptGroup>
    );
  };

  return (
    <ControlledSelect
      {...getCommonInputProps(gitProvider)}
      form={vcsForm}
      showSearch
      data-e2e="revisions-select"
      loading={revisionsLoading}
      // `loading` swaps every option for a placeholder, which would hide the listed and typed revisions for the
      // whole agent round trip, so a search only swaps the chevron for a spinner
      {...(searching && { suffixIcon: <LoadingOutlined data-e2e="revisions-searching" /> })}
      disabled={Boolean(!repository || disabled)}
      tooltip={tooltip}
      onSearch={allowUnlistedRevision ? setRevisionSearch : undefined}
      customNotFoundContent={<FormattedMessage id="templates.add.new.field.revisions.empty" />}>
      {RevisionsOptGroup('heads', 'branches', availableRevisions?.heads)}
      {RevisionsOptGroup('tags', 'tags', availableRevisions?.tags)}
      {allowUnlistedRevision && isUnlistedRevision && (
        <SelectOptGroup
          key="other"
          label={<FormattedMessage id="templates.add.new.field.revisions.group.other" />}
          data-e2e="git-vcs-revision-select-group-other">
          <SelectOption key={revisionSearch} value={revisionSearch}>
            {revisionSearch}
          </SelectOption>
        </SelectOptGroup>
      )}
    </ControlledSelect>
  );
};

const RevisionTextBox: React.FunctionComponent<RevisionsComponentProps> = ({
  vcsForm,
  gitProvider,
  disabled,
  tooltip
}) => {
  return (
    <ControlledTextInput
      {...getCommonInputProps(gitProvider)}
      data-e2e="revision-input"
      form={vcsForm}
      disabled={disabled}
      tooltip={tooltip}
      placeholder={`templates.add.new.field.revision.placeholder.${
        gitProvider === 'HelmRepository' ? 'helmRepo' : 'git'
      }`}
    />
  );
};

export default RevisionsSelectField;
